/**
 * Surveys (Beta) authoring — `/api/v1/surveys` create/update/questions/answers/publish.
 * Toast/branding rails and CSV import omitted (API gaps).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import { debounce } from 'lodash';
import {
  PortalDsAlert,
  PortalDsBadge,
  PortalDsButton,
  PortalDsCard,
  PortalDsField,
  PortalDsInput,
  PortalDsSegmented,
  PortalDsSelect,
  portalDs,
} from 'src/components/portal-design-system';
import { useSetPageTitle } from 'src/hooks/useSetPageTitle';
import { showNotification } from 'src/redux/notification/notification.slice';
import {
  useCreateSurveysApiV1AnswerMutation,
  useCreateSurveysApiV1QuestionMutation,
  useDeleteSurveysApiV1AnswerMutation,
  useDeleteSurveysApiV1QuestionMutation,
  useGetSurveysApiV1SurveyQuery,
  usePublishSurveysApiV1SurveyMutation,
  useUpdateSurveysApiV1AnswerMutation,
  useUpdateSurveysApiV1QuestionMutation,
  useUpdateSurveysApiV1SurveyMutation,
} from 'src/redux/survey/surveys-api-v1/surveys-api-v1.endpoints';
import { formatSurveyStatusLabel } from 'src/redux/survey/surveys-api-v1/surveys-api-v1.adapters';
import type { SurveysApiQuestion } from 'src/redux/survey/surveys-api-v1/surveys-api-v1.types';
import { useSurveysBetaExtClientId } from '../hooks/use-surveys-beta-ext-client-id';
import { surveyStatusPillVariant } from '../utils/survey-status-pill';
import { surveysBetaResultsPath } from '../utils/surveys-beta-navigation';
import {
  SurveysBetaSyncStatus,
  type SurveysBetaSyncState,
} from './surveys-beta-sync-status';
import '../surveys-beta.gaps';

const SURVEY_TYPE_OPTIONS = [
  { value: '', label: 'Select type…' },
  { value: 'web', label: 'Web' },
  { value: 'ai', label: 'AI' },
  { value: 'rtr', label: 'RTR' },
];

const QUESTION_TYPE_OPTIONS = [
  { value: 'single_select', label: 'Single choice' },
  { value: 'multi_select', label: 'Multiple choice' },
  { value: 'free_form', label: 'Freeform' },
] as const;

const META_SAVE_DEBOUNCE_MS = 400;

function questionTypeLabel(type: string): string {
  switch (type) {
    case 'multi_select':
      return 'Multiple choice';
    case 'free_form':
      return 'Freeform';
    default:
      return 'Single choice';
  }
}

const SurveysBetaEditPage = () => {
  useSetPageTitle('Edit survey');
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { clientId } = useParams<{ clientId?: string }>();
  const [searchParams] = useSearchParams();
  const extSurveyId = searchParams.get('ext-survey-id') ?? '';
  const extClientId = useSurveysBetaExtClientId();

  const { data: survey, isLoading, isError, refetch } = useGetSurveysApiV1SurveyQuery(
    { extSurveyId, extClientId },
    { skip: !extSurveyId, refetchOnMountOrArgChange: true }
  );

  const [updateSurvey, { isLoading: isSavingSurvey }] = useUpdateSurveysApiV1SurveyMutation();
  const [publishSurvey, { isLoading: isPublishing }] = usePublishSurveysApiV1SurveyMutation();
  const [createQuestion, { isLoading: isCreatingQuestion }] =
    useCreateSurveysApiV1QuestionMutation();
  const [updateQuestion] = useUpdateSurveysApiV1QuestionMutation();
  const [deleteQuestion] = useDeleteSurveysApiV1QuestionMutation();
  const [createAnswer] = useCreateSurveysApiV1AnswerMutation();
  const [updateAnswer] = useUpdateSurveysApiV1AnswerMutation();
  const [deleteAnswer] = useDeleteSurveysApiV1AnswerMutation();

  const [title, setTitle] = useState('');
  const [surveyType, setSurveyType] = useState('');
  const [expandedQuestionId, setExpandedQuestionId] = useState<string | null>(null);
  const [syncStatus, setSyncStatus] = useState<SurveysBetaSyncState>('idle');
  const saveInFlightRef = useRef(0);
  const metaHydratedForIdRef = useRef<string | null>(null);

  const beginSave = useCallback(() => {
    saveInFlightRef.current += 1;
    setSyncStatus('saving');
  }, []);

  const endSaveOk = useCallback(() => {
    saveInFlightRef.current = Math.max(0, saveInFlightRef.current - 1);
    if (saveInFlightRef.current === 0) {
      setSyncStatus('saved');
    }
  }, []);

  const endSaveError = useCallback(() => {
    saveInFlightRef.current = Math.max(0, saveInFlightRef.current - 1);
    setSyncStatus('error');
  }, []);

  useEffect(() => {
    if (!survey) {
      return;
    }
    if (metaHydratedForIdRef.current !== survey.ext_survey_id) {
      metaHydratedForIdRef.current = survey.ext_survey_id;
      setTitle(survey.title || '');
      setSurveyType(survey.survey_type || '');
      setSyncStatus('idle');
    }
    if (!expandedQuestionId && survey.questions[0]) {
      setExpandedQuestionId(survey.questions[0].id);
    }
  }, [survey, expandedQuestionId]);

  const questions = useMemo(() => {
    const list = survey?.questions ? [...survey.questions] : [];
    return list.sort((a, b) => a.question_order - b.question_order);
  }, [survey?.questions]);

  const checklist = useMemo(() => {
    const nameSet = Boolean(title.trim());
    const typeSet = Boolean(surveyType);
    const questionsReady =
      questions.length > 0 &&
      questions.every((question) => {
        if (!question.title?.trim()) {
          return false;
        }
        if (question.type === 'free_form') {
          return true;
        }
        return (question.answers?.length ?? 0) > 0;
      });
    return {
      nameSet,
      typeSet,
      questionsReady,
      remaining:
        [nameSet, typeSet, questionsReady].filter((item) => !item).length,
    };
  }, [title, surveyType, questions]);

  const notifyError = useCallback((error: unknown, fallback: string) => {
    const message = (error as { message?: string })?.message || fallback;
    dispatch(showNotification({ summary: message, theme: 'warning', sticky: true }));
  }, [dispatch]);

  const persistMeta = useCallback(
    async(nextTitle: string, nextType: string) => {
      if (!extSurveyId) {
        return;
      }
      const serverTitle = (survey?.title || '').trim();
      const serverType = survey?.survey_type || '';
      const normalisedTitle = nextTitle.trim() || 'Untitled';
      if (normalisedTitle === (serverTitle || 'Untitled') && nextType === serverType) {
        return;
      }
      beginSave();
      try {
        await updateSurvey({
          extSurveyId,
          extClientId,
          body: {
            title: normalisedTitle,
            survey_type: nextType || null,
          },
        }).unwrap();
        endSaveOk();
        refetch();
      } catch (error) {
        endSaveError();
        notifyError(error, 'Could not save draft');
      }
    },
    [
      beginSave,
      endSaveError,
      endSaveOk,
      extClientId,
      extSurveyId,
      notifyError,
      refetch,
      survey?.survey_type,
      survey?.title,
      updateSurvey,
    ]
  );

  const debouncedPersistMeta = useMemo(
    () => debounce((nextTitle: string, nextType: string) => {
      void persistMeta(nextTitle, nextType);
    }, META_SAVE_DEBOUNCE_MS),
    [persistMeta]
  );

  useEffect(() => () => {
    debouncedPersistMeta.cancel();
  }, [debouncedPersistMeta]);

  useEffect(() => {
    if (!extSurveyId || !survey || metaHydratedForIdRef.current !== survey.ext_survey_id) {
      return;
    }
    const serverTitle = (survey.title || '').trim();
    const serverType = survey.survey_type || '';
    const normalisedTitle = title.trim() || 'Untitled';
    if (normalisedTitle === (serverTitle || 'Untitled') && surveyType === serverType) {
      debouncedPersistMeta.cancel();
      if (saveInFlightRef.current === 0) {
        setSyncStatus((prev) => (prev === 'saving' ? 'saved' : prev));
      }
      return;
    }
    setSyncStatus('saving');
    debouncedPersistMeta(title, surveyType);
  }, [title, surveyType, extSurveyId, survey, debouncedPersistMeta]);

  const flushMetaSave = async() => {
    debouncedPersistMeta.cancel();
    await persistMeta(title, surveyType);
  };

  const handleSaveDraft = async() => {
    await flushMetaSave();
  };

  const handleLaunch = async() => {
    if (!extSurveyId) {
      return;
    }
    try {
      await flushMetaSave();
      const published = await publishSurvey({ extSurveyId, extClientId }).unwrap();
      setSyncStatus('saved');
      refetch();
      dispatch(
        showNotification({
          summary:
            published.survey_status === 'live'
              ? 'Survey launched'
              : `Survey status: ${formatSurveyStatusLabel(published.survey_status)}`,
          theme: 'success',
        })
      );
      if (published.survey_status === 'live' || published.is_published) {
        navigate(surveysBetaResultsPath(extSurveyId, clientId));
      }
    } catch (error) {
      notifyError(error, 'Could not launch survey');
    }
  };

  const handleAddQuestion = async() => {
    if (!extSurveyId) {
      return;
    }
    beginSave();
    try {
      const created = await createQuestion({
        extSurveyId,
        extClientId,
        body: { title: 'Untitled question', type: 'single_select' },
      }).unwrap();
      setExpandedQuestionId(created.id);
      endSaveOk();
      refetch();
    } catch (error) {
      endSaveError();
      notifyError(error, 'Could not add question');
    }
  };

  const handleUpdateQuestionField = async(
    question: SurveysApiQuestion,
    body: { title?: string; type?: string; shuffle?: string; has_feedback?: boolean }
  ) => {
    beginSave();
    try {
      await updateQuestion({
        extSurveyId,
        questionId: question.id,
        extClientId,
        body,
      }).unwrap();
      endSaveOk();
      refetch();
    } catch (error) {
      endSaveError();
      notifyError(error, 'Could not update question');
    }
  };

  const handleDeleteQuestion = async(questionId: string) => {
    beginSave();
    try {
      await deleteQuestion({ extSurveyId, questionId, extClientId }).unwrap();
      if (expandedQuestionId === questionId) {
        setExpandedQuestionId(null);
      }
      endSaveOk();
      refetch();
    } catch (error) {
      endSaveError();
      notifyError(error, 'Could not delete question');
    }
  };

  const handleAddAnswer = async(questionId: string) => {
    beginSave();
    try {
      await createAnswer({
        extSurveyId,
        questionId,
        extClientId,
        body: { title: 'New answer' },
      }).unwrap();
      endSaveOk();
      refetch();
    } catch (error) {
      endSaveError();
      notifyError(error, 'Could not add answer');
    }
  };

  const handleUpdateAnswerTitle = async(
    questionId: string,
    answerId: string,
    nextTitle: string
  ) => {
    beginSave();
    try {
      await updateAnswer({
        extSurveyId,
        questionId,
        answerId,
        extClientId,
        body: { title: nextTitle },
      }).unwrap();
      endSaveOk();
      refetch();
    } catch (error) {
      endSaveError();
      notifyError(error, 'Could not update answer');
    }
  };

  const handleDeleteAnswer = async(questionId: string, answerId: string) => {
    beginSave();
    try {
      await deleteAnswer({
        extSurveyId,
        questionId,
        answerId,
        extClientId,
      }).unwrap();
      endSaveOk();
      refetch();
    } catch (error) {
      endSaveError();
      notifyError(error, 'Could not delete answer');
    }
  };

  return (
    <>
      <div className="flex flex-col gap-5">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="flex flex-wrap items-center gap-3">
              <h1 className={portalDs.sheetTitle}>
                {title || 'Untitled'}
              </h1>
              {survey ? (
                <PortalDsBadge
                  pill
                  variant={surveyStatusPillVariant(survey.survey_status)}
                >
                  {formatSurveyStatusLabel(survey.survey_status)}
                </PortalDsBadge>
              ) : null}
            </div>
            <p className={portalDs.sheetDescription}>
              Changes save automatically
            </p>
          </div>
          <div className="flex flex-wrap gap-2">
            <PortalDsButton
              variant="secondary"
              disabled={isSavingSurvey || syncStatus === 'saving' || !extSurveyId}
              onClick={handleSaveDraft}
            >
              Save draft
            </PortalDsButton>
            <PortalDsButton
              variant="accent"
              disabled={isPublishing || !extSurveyId || checklist.remaining > 0}
              onClick={handleLaunch}
            >
              Launch survey
            </PortalDsButton>
          </div>
        </div>

        {!extSurveyId ? (
          <PortalDsAlert
            variant="warning"
            title="Missing survey"
          >
            Open this page from Surveys (Beta) after creating a survey.
          </PortalDsAlert>
        ) : null}
        {isError ? (
          <PortalDsAlert
            variant="danger"
            title="Could not load survey"
          >
            Check Surveys API entitlements and try again.
          </PortalDsAlert>
        ) : null}
        {isLoading ? (
          <p className={portalDs.bodySm}>
            Loading survey…
          </p>
        ) : null}

        {survey ? (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1fr)_280px]">
            <div className="flex flex-col gap-5">
              <PortalDsCard>
                <p className={portalDs.sectionLabel}>
                  Setup
                </p>
                <p className={cnHeading}>
                  Applies to the whole survey
                </p>
                <div className="mt-4 flex flex-col gap-4">
                  <PortalDsField
                    label="Survey name"
                    required
                    hint="Internal only. Include the wave number so results sort cleanly."
                  >
                    <PortalDsInput
                      value={title}
                      onChange={(event) => setTitle(event.target.value)}
                    />
                  </PortalDsField>
                  <PortalDsField
                    label="Survey type"
                    required
                    hint="Required — type sets the panel and the pricing tier."
                  >
                    <PortalDsSelect
                      value={surveyType}
                      onChange={(event) => setSurveyType(event.target.value)}
                    >
                      {SURVEY_TYPE_OPTIONS.map((option) => (
                        <option
                          key={option.value || 'empty'}
                          value={option.value}
                        >
                          {option.label}
                        </option>
                      ))}
                    </PortalDsSelect>
                  </PortalDsField>
                </div>
              </PortalDsCard>

              <PortalDsCard>
                <div className="mb-4 flex flex-wrap items-center justify-between gap-3">
                  <div>
                    <p className={portalDs.sectionLabel}>
                      Questions
                    </p>
                    <p className={portalDs.bodySm}>
                      {questions.length}
                      {' '}
                      question
                      {questions.length === 1 ? '' : 's'}
                    </p>
                  </div>
                  <PortalDsButton
                    size="sm"
                    variant="secondary"
                    disabled={isCreatingQuestion}
                    onClick={handleAddQuestion}
                  >
                    Add question
                  </PortalDsButton>
                </div>

                <div className="flex flex-col gap-3">
                  {questions.map((question, index) => {
                    const expanded = expandedQuestionId === question.id;
                    const answers = [...(question.answers || [])].sort(
                      (a, b) => a.display_order - b.display_order
                    );
                    return (
                      <div
                        key={question.id}
                        className="rounded-lg border border-[#EDEEF2] bg-[#F7F8FA] p-4"
                      >
                        <button
                          type="button"
                          className="flex w-full items-start justify-between gap-3 text-left"
                          onClick={() =>
                            setExpandedQuestionId(expanded ? null : question.id)
                          }
                        >
                          <div>
                            <p className="text-[13.5px] font-semibold text-[#0D0F1A]">
                              Q
                              {index + 1}
                              {' '}
                              {question.title || 'Untitled question'}
                            </p>
                            <p className={portalDs.caption}>
                              {questionTypeLabel(question.type)}
                              {' '}
                              ·
                              {answers.length}
                              {' '}
                              answer
                              {answers.length === 1 ? '' : 's'}
                            </p>
                          </div>
                          <span className={portalDs.caption}>
                            {expanded ? 'Collapse' : 'Edit'}
                          </span>
                        </button>

                        {expanded ? (
                          <div className="mt-4 flex flex-col gap-4 border-t border-[#EDEEF2] pt-4">
                            <PortalDsField
                              label="Question text"
                              required
                            >
                              <PortalDsInput
                                defaultValue={question.title}
                                key={`${question.id}-title`}
                                onBlur={(event) => {
                                  if (event.target.value !== question.title) {
                                    void handleUpdateQuestionField(question, {
                                      title: event.target.value,
                                    });
                                  }
                                }}
                              />
                            </PortalDsField>

                            <PortalDsField label="Answer type">
                              <PortalDsSegmented
                                ariaLabel="Answer type"
                                value={
                                  (QUESTION_TYPE_OPTIONS.find(
                                    (option) => option.value === question.type
                                  )?.value || 'single_select') as
                                  | 'single_select'
                                  | 'multi_select'
                                  | 'free_form'
                                }
                                options={[...QUESTION_TYPE_OPTIONS]}
                                onChange={(value) => {
                                  void handleUpdateQuestionField(question, {
                                    type: value,
                                  });
                                }}
                              />
                            </PortalDsField>

                            <label className={portalDs.formCheck}>
                              <input
                                type="checkbox"
                                className={portalDs.formCheckInput}
                                checked={question.shuffle === 'random'}
                                onChange={(event) => {
                                  void handleUpdateQuestionField(question, {
                                    shuffle: event.target.checked
                                      ? 'random'
                                      : 'no_shuffle',
                                  });
                                }}
                              />
                              <span className={portalDs.formCheckLabel}>
                                Randomize order
                              </span>
                            </label>

                            <label className={portalDs.formCheck}>
                              <input
                                type="checkbox"
                                className={portalDs.formCheckInput}
                                checked={Boolean(question.has_feedback)}
                                onChange={(event) => {
                                  void handleUpdateQuestionField(question, {
                                    has_feedback: event.target.checked,
                                  });
                                }}
                              />
                              <span className={portalDs.formCheckLabel}>
                                Ask why
                              </span>
                            </label>

                            {question.type !== 'free_form' ? (
                              <div className="flex flex-col gap-2">
                                <div className="flex items-center justify-between">
                                  <p className={portalDs.label}>
                                    Answers
                                    {' '}
                                    {answers.length}
                                    {' '}
                                    / 8 used
                                  </p>
                                  <PortalDsButton
                                    size="sm"
                                    variant="ghost"
                                    disabled={answers.length >= 8}
                                    onClick={() => handleAddAnswer(question.id)}
                                  >
                                    Add answer
                                  </PortalDsButton>
                                </div>
                                {answers.map((answer) => (
                                  <div
                                    key={answer.id}
                                    className="flex items-center gap-2"
                                  >
                                    <PortalDsInput
                                      defaultValue={answer.title || ''}
                                      key={`${answer.id}-title`}
                                      onBlur={(event) => {
                                        if (event.target.value !== (answer.title || '')) {
                                          void handleUpdateAnswerTitle(
                                            question.id,
                                            answer.id,
                                            event.target.value
                                          );
                                        }
                                      }}
                                    />
                                    <PortalDsButton
                                      size="sm"
                                      variant="dangerGhost"
                                      onClick={() =>
                                        handleDeleteAnswer(question.id, answer.id)
                                      }
                                    >
                                      Remove
                                    </PortalDsButton>
                                  </div>
                                ))}
                              </div>
                            ) : null}

                            <div className="flex justify-end">
                              <PortalDsButton
                                size="sm"
                                variant="dangerGhost"
                                onClick={() => handleDeleteQuestion(question.id)}
                              >
                                Delete question
                              </PortalDsButton>
                            </div>
                          </div>
                        ) : null}
                      </div>
                    );
                  })}
                </div>
              </PortalDsCard>
            </div>

            <aside className="flex flex-col gap-4">
              <PortalDsCard>
                <p className={portalDs.sectionLabel}>
                  Before you launch
                </p>
                <p className={portalDs.caption}>
                  {checklist.remaining === 0
                    ? 'Ready to launch'
                    : `${checklist.remaining} left`}
                </p>
                <ul className="mt-4 flex flex-col gap-2 text-[13px] text-[#5E6278]">
                  <li>
                    {checklist.nameSet ? '✓' : '○'}
                    {' '}
                    Name set
                  </li>
                  <li>
                    {checklist.questionsReady ? '✓' : '○'}
                    {' '}
                    Questions with answers
                  </li>
                  <li>
                    {checklist.typeSet ? '✓' : '○'}
                    {' '}
                    Survey type chosen
                  </li>
                </ul>
                <PortalDsButton
                  className="mt-4 w-full"
                  variant="accent"
                  disabled={isPublishing || checklist.remaining > 0}
                  onClick={handleLaunch}
                >
                  Launch survey
                </PortalDsButton>
                <p className={`${portalDs.caption} mt-3`}>
                  Collection starts immediately. Questions lock once the first
                  response lands.
                </p>
              </PortalDsCard>
            </aside>
          </div>
        ) : null}
      </div>
      <SurveysBetaSyncStatus status={syncStatus} />
    </>
  );
};

const cnHeading = 'text-base font-semibold tracking-[-0.2px] text-[#0D0F1A]';

export default SurveysBetaEditPage;
